import { TILE_SIZE } from '@/sprites/tiles';
import type { Tilemap } from '@/world/tilemap';

/**
 * Animals in a pen.
 *
 * They wander because a pen of statues is not company. Movement is a slow
 * random walk with long pauses rather than anything clever — from across a
 * field, "alive" reads as "occasionally somewhere else", and a pathfinder here
 * would be cost with no visible return.
 */

export type AnimalKind = 'chicken';

export interface AnimalSave {
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly name: string;
  readonly fedOnDay: number | null;
  readonly producedOnDay: number | null;
  readonly friendship: number;
}

const WANDER_SPEED = 12; // px/sec, an unhurried amble
const MIN_PAUSE = 1.5;
const MAX_PAUSE = 5;
const MAX_FRIENDSHIP = 100;

export class Animal {
  x: number;
  y: number;
  readonly kind: AnimalKind;
  readonly name: string;

  /** Day it was last fed, and the day it last laid something. */
  fedOnDay: number | null = null;
  producedOnDay: number | null = null;
  friendship = 0;

  /** Set when produce is waiting to be collected. */
  hasProduce = false;

  private targetX: number;
  private targetY: number;
  private pause: number;
  private readonly random: () => number;

  constructor(kind: AnimalKind, x: number, y: number, name: string, random = Math.random) {
    this.kind = kind;
    this.x = x;
    this.y = y;
    this.name = name;
    this.targetX = x;
    this.targetY = y;
    this.random = random;
    this.pause = MIN_PAUSE + random() * (MAX_PAUSE - MIN_PAUSE);
  }

  get tileX(): number {
    return Math.floor((this.x + TILE_SIZE / 2) / TILE_SIZE);
  }

  get tileY(): number {
    return Math.floor((this.y + TILE_SIZE / 2) / TILE_SIZE);
  }

  /** Wander inside `bounds`, never leaving the pen or entering solid tiles. */
  update(dt: number, map: Tilemap, bounds: PenBounds): void {
    if (this.pause > 0) {
      this.pause -= dt;
      if (this.pause <= 0) this.chooseTarget(bounds);
      return;
    }

    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    const distance = Math.hypot(dx, dy);

    if (distance < 1) {
      this.pause = MIN_PAUSE + this.random() * (MAX_PAUSE - MIN_PAUSE);
      return;
    }

    const step = Math.min(WANDER_SPEED * dt, distance);
    const nextX = this.x + (dx / distance) * step;
    const nextY = this.y + (dy / distance) * step;

    // Tested against the sprite's centre, matching `tileX`/`tileY`. Using the
    // raw top-left here let an animal report standing on a wall tile it had
    // never been checked against.
    const nextTileX = Math.floor((nextX + TILE_SIZE / 2) / TILE_SIZE);
    const nextTileY = Math.floor((nextY + TILE_SIZE / 2) / TILE_SIZE);

    // Give up on a target behind a wall rather than pressing into it forever.
    if (map.isSolidTile(nextTileX, nextTileY)) {
      this.pause = MIN_PAUSE;
      this.chooseTarget(bounds);
      return;
    }

    this.x = nextX;
    this.y = nextY;
  }

  /** Pick somewhere new inside the pen to amble towards. */
  private chooseTarget(bounds: PenBounds): void {
    const tileX = bounds.minX + this.random() * (bounds.maxX - bounds.minX);
    const tileY = bounds.minY + this.random() * (bounds.maxY - bounds.minY);
    this.targetX = tileX * TILE_SIZE;
    this.targetY = tileY * TILE_SIZE;
  }

  /** Feed for the day. Returns false if it has already eaten. */
  feed(dayIndex: number): boolean {
    if (this.fedOnDay === dayIndex) return false;
    this.fedOnDay = dayIndex;
    this.friendship = Math.min(MAX_FRIENDSHIP, this.friendship + 4);
    return true;
  }

  /** Collect whatever is waiting. Returns false if there is nothing. */
  collect(): boolean {
    if (!this.hasProduce) return false;
    this.hasProduce = false;
    this.friendship = Math.min(MAX_FRIENDSHIP, this.friendship + 1);
    return true;
  }

  /**
   * A fed animal lays overnight.
   *
   * Fed yesterday, produce this morning: the reward lands on the walk out, not
   * the moment you feed, so the routine has a rhythm rather than a queue.
   */
  advanceDay(previousDay: number): void {
    if (this.fedOnDay === previousDay) {
      this.hasProduce = true;
      this.producedOnDay = previousDay + 1;
    }
    this.fedOnDay = null;
  }

  toSave(): AnimalSave {
    return {
      kind: this.kind,
      x: this.x,
      y: this.y,
      name: this.name,
      fedOnDay: this.fedOnDay,
      producedOnDay: this.hasProduce ? this.producedOnDay : null,
      friendship: this.friendship,
    };
  }
}

export interface PenBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export class Livestock {
  readonly animals: Animal[] = [];

  constructor(readonly bounds: PenBounds) {}

  static starting(bounds: PenBounds, random = Math.random): Livestock {
    const livestock = new Livestock(bounds);
    const centreX = (bounds.minX + bounds.maxX) / 2;
    const centreY = (bounds.minY + bounds.maxY) / 2;
    livestock.animals.push(
      new Animal('chicken', centreX * TILE_SIZE, centreY * TILE_SIZE, 'Biscuit', random),
      new Animal('chicken', (centreX + 1) * TILE_SIZE, centreY * TILE_SIZE, 'Pebble', random),
    );
    return livestock;
  }

  update(dt: number, map: Tilemap): void {
    for (const animal of this.animals) animal.update(dt, map, this.bounds);
  }

  advanceDay(previousDay: number): void {
    for (const animal of this.animals) animal.advanceDay(previousDay);
  }

  /** Nearest animal within one tile of the given tile, if any. */
  nearestTo(tileX: number, tileY: number): Animal | null {
    for (const animal of this.animals) {
      if (Math.abs(animal.tileX - tileX) <= 1 && Math.abs(animal.tileY - tileY) <= 1) {
        return animal;
      }
    }
    return null;
  }

  toSave(): AnimalSave[] {
    return this.animals.map((animal) => animal.toSave());
  }

  static fromSave(entries: unknown, bounds: PenBounds, random = Math.random): Livestock {
    if (!Array.isArray(entries) || entries.length === 0) {
      return Livestock.starting(bounds, random);
    }

    const livestock = new Livestock(bounds);
    for (const entry of entries as AnimalSave[]) {
      if (!entry || entry.kind !== 'chicken') continue;
      if (!Number.isFinite(entry.x) || !Number.isFinite(entry.y)) continue;

      const animal = new Animal('chicken', entry.x, entry.y, entry.name || 'Chicken', random);
      animal.fedOnDay = typeof entry.fedOnDay === 'number' ? entry.fedOnDay : null;
      animal.friendship = clamp(Number(entry.friendship) || 0, 0, MAX_FRIENDSHIP);
      animal.hasProduce = typeof entry.producedOnDay === 'number';
      animal.producedOnDay = animal.hasProduce ? entry.producedOnDay : null;
      livestock.animals.push(animal);
    }

    return livestock.animals.length > 0 ? livestock : Livestock.starting(bounds, random);
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
